import { type ReactNode, useMemo } from 'react';
import { useCreateRealtimeSessionMutation } from '@features/transcription';
import { useEventTracking } from '@services/statsig';

import { listMicrophones, microphonePreference } from '../audio/microphones';
import { browserSupportsDictation, startPcmCapture } from '../audio/pcmCapture';
import { DictationRuntimeProvider } from '../context';
import { createAssemblyAiStreamingEngine } from '../engines/assemblyAiStreamingEngine';
import { DictationError, type DictationRuntime } from '../types';

/** The token is used immediately; a minute covers a slow connect. */
const TOKEN_TTL_SECONDS = 60;

/**
 * The application's dictation runtime: backend-minted AssemblyAI token, the
 * encounter recorder's microphone stack, and product events. Mount once, high
 * in the authenticated tree; every `DictationTextField` and `DictationButton`
 * below it lights up.
 */
export function AppDictationRuntime({ children }: { children: ReactNode }) {
  const [createSession] = useCreateRealtimeSessionMutation();
  const { dictation } = useEventTracking();

  const runtime = useMemo<DictationRuntime>(
    () => ({
      enabled: browserSupportsDictation(),
      requestToken: async () => {
        try {
          const response = await createSession({
            createRealtimeSessionRequest: { expires_in_seconds: TOKEN_TTL_SECONDS },
          }).unwrap();
          return { token: response.token, expiresInSeconds: response.expires_in_seconds };
        } catch {
          throw new DictationError(
            'token',
            'Dictation could not start. Check your connection and try again.',
          );
        }
      },
      createEngine: createAssemblyAiStreamingEngine,
      startCapture: startPcmCapture,
      listMicrophones,
      microphonePreference,
      track: event => {
        if (event.type === 'started') dictation.started(event.surface);
        else dictation.exited(event.surface, event.reason, event.durationMs, event.turnCount);
      },
    }),
    [createSession, dictation],
  );

  return <DictationRuntimeProvider runtime={runtime}>{children}</DictationRuntimeProvider>;
}
